import { useState, useEffect } from 'react';
import { Users, Mail, BarChart2, Send, RefreshCw, Shield, Key, Ban, CheckCircle, Eye, ChevronLeft, Trash2, AlertTriangle, UserX } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

function StatCard({ label, value, sub, icon: Icon, color = 'var(--blue)' }) {
  return (
    <div className="card" style={{ padding: '18px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 11, color: 'var(--text2)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={13} color={color} />
        </div>
      </div>
      <div style={{ fontSize: 26, fontWeight: 600 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [activity, setActivity] = useState([]);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [resetModal, setResetModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [showPlatformReset, setShowPlatformReset] = useState(false);
  const [platformResetting, setPlatformResetting] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [listLeads, setListLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);

  const load = async () => {
    try {
      const [sRes, uRes, cRes, aRes] = await Promise.all([
        api.get('/api/admin/stats'),
        api.get('/api/admin/users'),
        api.get('/api/admin/campaigns'),
        api.get('/api/admin/activity'),
      ]);
      setStats(sRes.data);
      setUsers(uRes.data.users || []);
      setCampaigns(cRes.data.campaigns || []);
      setActivity(aRes.data.activity || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load admin data');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const platformReset = async () => {
    setPlatformResetting(true);
    try {
      await api.post('/api/admin/reset');
      toast.success('Platform reset! All campaigns and leads cleared.');
      setShowPlatformReset(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reset failed');
    } finally { setPlatformResetting(false); }
  };


  const deleteUser = async (userId, userName, userEmail) => {
    if (!window.confirm('PERMANENTLY DELETE ' + userName + '? This will delete their account, all linked Gmail and Brevo accounts, all campaigns, leads and data. This CANNOT be undone!')) return;
    const confirm2 = window.prompt('Type DELETE to confirm:');
    if (confirm2 !== 'DELETE') { toast.error('Deletion cancelled'); return; }
    try {
      await api.delete('/api/admin/users/' + userId);
      toast.success(userEmail + ' permanently deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const resetUserData = async (userId, userName) => {
    if (!window.confirm('Clear all campaigns and leads for ' + userName + '? Email accounts kept.')) return;
    try {
      await api.post('/api/admin/users/' + userId + '/reset-data');
      toast.success(userName + "'s data cleared");
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reset user data');
    }
  };

  const viewUser = async (user) => {
    setSelectedUser(user);
    setUserDetail(null);
    setSelectedList(null);
    setListLeads([]);
    setUserDetailLoading(true);
    try {
      const res = await api.get(`/api/admin/users/${user.id}`);
      setUserDetail(res.data);
    } catch (err) {
      toast.error('Failed to load user details');
    } finally { setUserDetailLoading(false); }
  };

  const viewLeads = async (listId) => {
    setLeadsLoading(true);
    setSelectedList(listId);
    try {
      const res = await api.get(`/api/admin/users/${selectedUser.id}/leads/${listId}`);
      setListLeads(res.data.leads || []);
    } catch { toast.error('Failed to load leads'); }
    finally { setLeadsLoading(false); }
  };

  const suspendUser = async (userId, suspend) => {
    const action = suspend ? 'suspend' : 'unsuspend';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    try {
      await api.post(`/api/admin/users/${userId}/${action}`);
      toast.success(`User ${action}ed successfully`);
      load();
      if (userDetail) {
        const res = await api.get(`/api/admin/users/${userId}`);
        setUserDetail(res.data);
        setSelectedUser(res.data.user);
      }
    } catch (err) { toast.error(err.response?.data?.error || `Failed to ${action}`); }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 6) return toast.error('Password must be at least 6 characters');
    setResetting(true);
    try {
      await api.post(`/api/admin/users/${resetModal.id}/reset-password`, { newPassword });
      toast.success(`Password reset for ${resetModal.name}`);
      setResetModal(null);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reset password');
    } finally { setResetting(false); }
  };

  const statusBadge = (s) => {
    const map = { running: 'badge-blue', paused: 'badge-amber', completed: 'badge-green', draft: 'badge-gray', stopped: 'badge-red' };
    return <span className={`badge ${map[s] || 'badge-gray'}`}>{s}</span>;
  };

  if (loading) return <div style={{ color: 'var(--text3)', fontSize: 13, marginTop: 60, textAlign: 'center' }}>Loading admin data…</div>;

  // User detail view
  if (selectedUser) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => { setSelectedUser(null); setUserDetail(null); setSelectedList(null); }}>
            <ChevronLeft size={14} /> Back
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 style={{ fontSize: 18, fontWeight: 600 }}>{selectedUser.name}</h1>
              {selectedUser.is_suspended && <span className="badge badge-red">suspended</span>}
              {selectedUser.is_admin && <span className="badge badge-blue">admin</span>}
            </div>
            <p style={{ fontSize: 13, color: 'var(--text2)' }}>{selectedUser.email}</p>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => { setResetModal(selectedUser); setNewPassword(''); }}>
              <Key size={12} /> Reset password
            </button>
            {selectedUser.is_suspended ? (
              <button className="btn btn-primary btn-sm" onClick={() => suspendUser(selectedUser.id, false)}>
                <CheckCircle size={12} /> Unsuspend
              </button>
            ) : (
              <button className="btn btn-danger btn-sm" onClick={() => suspendUser(selectedUser.id, true)}>
                <Ban size={12} /> Suspend user
              </button>
            )}
          </div>
        </div>

        {userDetailLoading ? (
          <div style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', marginTop: 40 }}>Loading…</div>
        ) : userDetail ? (
          <>
            {/* Stats row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
              <StatCard label="Campaigns" value={userDetail.campaigns.length} icon={Mail} />
              <StatCard label="Emails sent" value={userDetail.recentEmails.length} sub="recent 30" icon={Send} color="var(--green)" />
              <StatCard label="Lead lists" value={userDetail.lists.length} icon={Users} color="var(--amber)" />
              <StatCard label="Gmail accounts" value={userDetail.accounts.length} icon={BarChart2} color="var(--blue)" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              {/* Campaigns */}
              <div className="card">
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 500 }}>Campaigns</div>
                {userDetail.campaigns.length === 0 ? (
                  <div style={{ padding: 20, color: 'var(--text3)', fontSize: 12 }}>No campaigns yet</div>
                ) : userDetail.campaigns.map(c => (
                  <div key={c.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{c.list_name} · {parseInt(c.emails_sent) || 0} sent</div>
                    </div>
                    {statusBadge(c.status)}
                  </div>
                ))}
              </div>

              {/* Lead lists */}
              <div className="card">
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 500 }}>Lead lists</div>
                {userDetail.lists.length === 0 ? (
                  <div style={{ padding: 20, color: 'var(--text3)', fontSize: 12 }}>No lead lists yet</div>
                ) : userDetail.lists.map(l => (
                  <div key={l.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                    onClick={() => viewLeads(l.id)}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{l.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{l.lead_count || 0} contacts</div>
                    </div>
                    <Eye size={13} color="var(--blue)" />
                  </div>
                ))}
              </div>
            </div>

            {/* Leads table */}
            {selectedList && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 500 }}>
                  Leads in list
                  <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 8 }}>{listLeads.length} contacts</span>
                </div>
                {leadsLoading ? (
                  <div style={{ padding: 20, color: 'var(--text3)', fontSize: 12 }}>Loading leads…</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        {['Email', 'Name', 'Company', 'Status'].map(h => (
                          <th key={h} style={{ padding: '8px 16px', textAlign: 'left', fontSize: 11, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {listLeads.map(l => (
                        <tr key={l.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '8px 16px', fontSize: 12 }}>{l.email}</td>
                          <td style={{ padding: '8px 16px', fontSize: 12 }}>{[l.first_name, l.last_name].filter(Boolean).join(' ') || '—'}</td>
                          <td style={{ padding: '8px 16px', fontSize: 12, color: 'var(--text2)' }}>{l.company || '—'}</td>
                          <td style={{ padding: '8px 16px' }}><span className={`badge ${l.status === 'active' ? 'badge-green' : 'badge-gray'}`}>{l.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Gmail accounts */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 500 }}>Linked Gmail accounts</div>
              {userDetail.accounts.length === 0 ? (
                <div style={{ padding: 20, color: 'var(--text3)', fontSize: 12 }}>No accounts linked</div>
              ) : userDetail.accounts.map(a => (
                <div key={a.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--blue-dim)', color: '#93c5fd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600 }}>
                    {a.email.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13 }}>{a.email}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{a.daily_sent_count || 0} sent today</div>
                  </div>
                  <span className={`badge ${a.is_active ? 'badge-green' : 'badge-gray'}`}>{a.is_active ? 'active' : 'disabled'}</span>
                </div>
              ))}
            </div>

            {/* Recent emails */}
            <div className="card">
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 500 }}>Recent emails sent</div>
              {userDetail.recentEmails.length === 0 ? (
                <div style={{ padding: 20, color: 'var(--text3)', fontSize: 12 }}>No emails sent yet</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['To', 'Subject', 'Campaign', 'From', 'Status', 'Sent at'].map(h => (
                        <th key={h} style={{ padding: '8px 16px', textAlign: 'left', fontSize: 11, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {userDetail.recentEmails.map((e, i) => {
                      const sc = { sent: 'badge-blue', opened: 'badge-green', replied: 'badge-amber', bounced: 'badge-red', clicked: 'badge-blue' };
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '8px 16px', fontSize: 12 }}>{e.to_email}</td>
                          <td style={{ padding: '8px 16px', fontSize: 12, color: 'var(--text2)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.subject}</td>
                          <td style={{ padding: '8px 16px', fontSize: 12 }}>{e.campaign_name}</td>
                          <td style={{ padding: '8px 16px', fontSize: 11, color: 'var(--text3)' }}>{e.from_account}</td>
                          <td style={{ padding: '8px 16px' }}><span className={`badge ${sc[e.status] || 'badge-gray'}`}>{e.status}</span></td>
                          <td style={{ padding: '8px 16px', fontSize: 11, color: 'var(--text3)' }}>{new Date(e.sent_at).toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        ) : null}

        {/* Reset Password Modal */}
        {resetModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div className="card" style={{ width: 400, padding: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <Key size={16} color="var(--blue)" />
                <h2 style={{ fontSize: 16, fontWeight: 600 }}>Reset password</h2>
              </div>
              <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
                Setting new password for <strong>{resetModal.name}</strong>
              </p>
              <input className="input" type="password" placeholder="New password (min 6 characters)" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={{ marginBottom: 16 }} />
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="btn btn-ghost" onClick={() => { setResetModal(null); setNewPassword(''); }}>Cancel</button>
                <button className="btn btn-primary" onClick={resetPassword} disabled={resetting}>
                  {resetting ? 'Resetting…' : 'Reset password'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Main admin dashboard
  const tabs = ['overview', 'users', 'campaigns', 'activity'];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Shield size={18} color="var(--blue)" />
            <h1 style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.4px' }}>Admin Dashboard</h1>
          </div>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 3 }}>Full platform overview</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className='btn btn-ghost btn-sm' onClick={load}><RefreshCw size={13} /> Refresh</button>
          <button className='btn btn-danger btn-sm' onClick={() => setShowPlatformReset(true)}>
            <Trash2 size={13} /> Reset platform
          </button>
        </div>
      </div>

      {showPlatformReset && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className='card' style={{ width: 440, padding: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--red-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AlertTriangle size={18} color='var(--red)' />
              </div>
              <h2 style={{ fontSize: 16, fontWeight: 600 }}>Reset entire platform?</h2>
            </div>
            <div style={{ background: 'var(--red-dim)', border: '1px solid #7f1d1d', borderRadius: 'var(--radius-sm)', padding: '12px 14px', marginBottom: 20, fontSize: 13, color: '#fca5a5', lineHeight: 1.6 }}>
              <strong>This will permanently delete:</strong><br />
              All campaigns, lead lists, contacts, email history and analytics<br /><br />
              <strong>This will NOT delete:</strong><br />
              User accounts and linked Gmail accounts
            </div>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20 }}>This cannot be undone. Are you sure?</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className='btn btn-ghost' onClick={() => setShowPlatformReset(false)} disabled={platformResetting}>Cancel</button>
              <button className='btn btn-danger' onClick={platformReset} disabled={platformResetting}>
                <Trash2 size={13} /> {platformResetting ? 'Resetting...' : 'Yes, reset everything'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: 'none', border: 'none',
            color: tab === t ? 'var(--text)' : 'var(--text2)',
            fontSize: 13, fontWeight: tab === t ? 500 : 400,
            padding: '8px 14px',
            borderBottom: tab === t ? '2px solid var(--blue)' : '2px solid transparent',
            marginBottom: -1, cursor: 'pointer', textTransform: 'capitalize',
          }}>{t}</button>
        ))}
      </div>

      {tab === 'overview' && stats && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
            <StatCard label="Total users" value={parseInt(stats.users.count)} sub={`${stats.users.suspended || 0} suspended`} icon={Users} color="var(--blue)" />
            <StatCard label="Total campaigns" value={parseInt(stats.campaigns.count)} sub={`${stats.campaigns.running} running`} icon={Mail} color="var(--amber)" />
            <StatCard label="Emails sent" value={parseInt(stats.emails.total_sent).toLocaleString()} sub={`${stats.emails.total_opened} opened`} icon={Send} color="var(--green)" />
            <StatCard label="Linked accounts" value={parseInt(stats.accounts.count)} icon={BarChart2} color="var(--blue)" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            <div className="card" style={{ padding: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 14 }}>Email performance</div>
              {[
                { label: 'Sent', value: stats.emails.total_sent, color: 'var(--blue)' },
                { label: 'Opened', value: stats.emails.total_opened, color: 'var(--green)' },
                { label: 'Replied', value: stats.emails.total_replied, color: 'var(--amber)' },
                { label: 'Bounced', value: stats.emails.total_bounced, color: 'var(--red)' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text2)' }}>{label}</span>
                  <span style={{ fontWeight: 500, color }}>{parseInt(value).toLocaleString()}</span>
                </div>
              ))}
            </div>
            <div className="card" style={{ padding: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 14 }}>Campaigns</div>
              {[
                { label: 'Running', value: stats.campaigns.running, color: 'var(--blue)' },
                { label: 'Completed', value: stats.campaigns.completed, color: 'var(--green)' },
                { label: 'Total', value: stats.campaigns.count, color: 'var(--text)' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text2)' }}>{label}</span>
                  <span style={{ fontWeight: 500, color }}>{parseInt(value)}</span>
                </div>
              ))}
            </div>
            <div className="card" style={{ padding: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 14 }}>Users</div>
              {[
                { label: 'Total', value: stats.users.count, color: 'var(--text)' },
                { label: 'New this week', value: stats.users.new_this_week, color: 'var(--green)' },
                { label: 'Suspended', value: stats.users.suspended || 0, color: 'var(--red)' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text2)' }}>{label}</span>
                  <span style={{ fontWeight: 500, color }}>{parseInt(value)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {tab === 'users' && (
        <div className="card">
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 500 }}>
            {users.length} registered users
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['User', 'Campaigns', 'Emails sent', 'Accounts', 'Joined', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '9px 20px', textAlign: 'left', fontSize: 11, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border)', opacity: u.is_suspended ? 0.6 : 1 }}>
                  <td style={{ padding: '12px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: u.is_suspended ? 'var(--red-dim)' : 'var(--blue-dim)', color: u.is_suspended ? '#fca5a5' : '#93c5fd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
                        {u.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {u.name}
                          {u.is_admin && <span className="badge badge-blue" style={{ fontSize: 9 }}>admin</span>}
                          {u.is_suspended && <span className="badge badge-red" style={{ fontSize: 9 }}>suspended</span>}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 20px', fontSize: 13 }}>{u.campaign_count}</td>
                  <td style={{ padding: '12px 20px', fontSize: 13 }}>{parseInt(u.emails_sent).toLocaleString()}</td>
                  <td style={{ padding: '12px 20px', fontSize: 13 }}>{u.account_count}</td>
                  <td style={{ padding: '12px 20px', fontSize: 12, color: 'var(--text3)' }}>{new Date(u.created_at).toLocaleDateString()}</td>
                  <td style={{ padding: '12px 20px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => viewUser(u)} title="View details">
                        <Eye size={12} /> View
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setResetModal(u); setNewPassword(''); }} title="Reset password">
                        <Key size={12} />
                      </button>
                      {u.is_suspended ? (
                        <button className='btn btn-primary btn-sm' onClick={() => suspendUser(u.id, false)} title='Unsuspend'>
                          <CheckCircle size={12} />
                        </button>
                      ) : (
                        <button className='btn btn-danger btn-sm' onClick={() => suspendUser(u.id, true)} title='Suspend'>
                          <Ban size={12} />
                        </button>
                      )}
                      <button className='btn btn-ghost btn-sm' onClick={() => resetUserData(u.id, u.name)} title='Reset user data' style={{ color: 'var(--red)' }}></button>
                      <button className='btn btn-danger btn-sm' onClick={() => deleteUser(u.id, u.name, u.email)} title='Delete user permanently'>
                        <UserX size={12} />
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'campaigns' && (
        <div className="card">
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 500 }}>
            {campaigns.length} campaigns across all users
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Campaign', 'Owner', 'Status', 'Sent', 'Opened', 'Replied', 'Created'].map(h => (
                  <th key={h} style={{ padding: '9px 20px', textAlign: 'left', fontSize: 11, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {campaigns.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '11px 20px' }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{c.list_name}</div>
                  </td>
                  <td style={{ padding: '11px 20px' }}>
                    <div style={{ fontSize: 12 }}>{c.user_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{c.user_email}</div>
                  </td>
                  <td style={{ padding: '11px 20px' }}>{statusBadge(c.status)}</td>
                  <td style={{ padding: '11px 20px', fontSize: 13 }}>{parseInt(c.emails_sent) || 0}</td>
                  <td style={{ padding: '11px 20px', fontSize: 13, color: 'var(--green)' }}>{parseInt(c.emails_opened) || 0}</td>
                  <td style={{ padding: '11px 20px', fontSize: 13, color: 'var(--amber)' }}>{parseInt(c.emails_replied) || 0}</td>
                  <td style={{ padding: '11px 20px', fontSize: 12, color: 'var(--text3)' }}>{new Date(c.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'activity' && (
        <div className="card">
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 500 }}>
            Recent platform activity (last 50)
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['To', 'From', 'Campaign', 'User', 'Status', 'Time'].map(h => (
                  <th key={h} style={{ padding: '9px 20px', textAlign: 'left', fontSize: 11, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activity.map((a, i) => {
                const sc = { sent: 'badge-blue', opened: 'badge-green', replied: 'badge-amber', bounced: 'badge-red', clicked: 'badge-blue' };
                return (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '9px 20px', fontSize: 12 }}>{a.to_email}</td>
                    <td style={{ padding: '9px 20px', fontSize: 11, color: 'var(--text3)' }}>{a.from_account}</td>
                    <td style={{ padding: '9px 20px', fontSize: 12 }}>{a.campaign_name}</td>
                    <td style={{ padding: '9px 20px', fontSize: 12 }}>{a.user_name}</td>
                    <td style={{ padding: '9px 20px' }}><span className={`badge ${sc[a.status] || 'badge-gray'}`}>{a.status}</span></td>
                    <td style={{ padding: '9px 20px', fontSize: 11, color: 'var(--text3)' }}>{new Date(a.sent_at).toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {resetModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: 400, padding: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <Key size={16} color="var(--blue)" />
              <h2 style={{ fontSize: 16, fontWeight: 600 }}>Reset password</h2>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
              Setting new password for <strong>{resetModal.name}</strong> ({resetModal.email})
            </p>
            <input className="input" type="password" placeholder="New password (min 6 characters)" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={{ marginBottom: 16 }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => { setResetModal(null); setNewPassword(''); }}>Cancel</button>
              <button className="btn btn-primary" onClick={resetPassword} disabled={resetting}>
                {resetting ? 'Resetting…' : 'Reset password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
